import React, { FunctionComponent } from 'react';
import { Menu } from 'semantic-ui-react';
import { map } from 'ramda';
import withStyles from 'react-jss';
import { FormattedNumber } from 'react-intl';
import classnames from 'classnames';
import { NavLink } from 'react-router-dom';

import { ClassProp } from 'helpers/types';
import { abbreviateNumber } from 'helpers/stringFunctions';

import { PageRoutes } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/KeyMetricGraphs.helpers';
import { KeyMetrics } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/QuickLook/QuickLook';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { useSplitIO } from 'components/Splitio/useSplitIO';
import {
  useMerchantOverview,
  UseMerchantOverview,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';

import KeyMetricStat from '../KeyMetricStat';
import ROASKeyMetrics from '../ROASKeyMetric';
import styles from './KeyMetrics.styles';

type Props = {
  classes: ClassProp<typeof styles>;
  keyMetrics: KeyMetrics;
  showDeltas: boolean;
  locked?: boolean;
};

type Value = { value: number };

const renderString = (prefix?: string) => ({ value }: Value) => (
  <span>
    {prefix}
    {abbreviateNumber(value, 2)}
  </span>
);

const getROASAmount = ({ value, isSecondary }: any) => {
  if (!value) return null;

  return isSecondary ? (
    <span>
      <FormattedNumber
        value={value}
        style="currency"
        currency="USD"
        currencyDisplay="symbol"
      />
    </span>
  ) : (
    <span>
      <FormattedNumber value={value} maximumFractionDigits={0} />X
    </span>
  );
};

const getKeyMetrics = (isHidden: boolean, showAttribution: boolean) => [
  {
    label: 'Sales',
    hash: 'Sales',
    key: 'revenue',
    Component: renderString('$'),
    info: 'Total sales (includes pending and cleared)',
    secondaryText: 'cleared',
  },
  {
    label: 'Transactions',
    hash: 'Transactions',
    key: 'transactionCount',
    Component: FormattedNumber,
    options: {
      style: 'decimal',
      maximumFractionDigits: 1,
    },
    info: 'Number of transactions (includes pending and cleared)',
    secondaryText: 'cleared',
  },
  {
    label: 'AOV',
    hash: 'AOV',
    key: 'aov',
    Component: FormattedNumber,
    options: {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'symbol',
    },
    info: 'Average Order Value',
  },
  {
    label: 'Attribution',
    hash: 'Attribution',
    key: 'attribution',
    Component: FormattedNumber,
    hidden: !showAttribution,
    options: {
      style: 'percent',
      maximumFractionDigits: 1,
    },
    info: 'Percentage of transactions exposed to a brand impression',
  },
  {
    label: 'Repeat Txns',
    hash: 'Frequency',
    key: 'frequency',
    Component: FormattedNumber,
    options: {
      style: 'percent',
      maximumFractionDigits: 1,
    },
    info: '% of users who transacted at this merchant 2+ times',
  },
  {
    label: 'Lift',
    hash: 'Lift',
    key: 'lift',
    Component: FormattedNumber,
    hidden: isHidden,
    disabled: true,
    info: 'Percent increase in sales relative to control group',
    options: {
      style: 'percent',
      maximumFractionDigits: 1,
    },
    defaultText: 'No Control Groups',
  },
  {
    label: 'Roas',
    hash: 'Roas',
    key: 'roas',
    Component: getROASAmount,
    disabled: true,
    info:
      'Return On Ad Spend: Total sales divided by total campaign fee. (includes pending and cleared)',
    secondaryText: 'Total Spend',
  },
];

const renderMetric = (
  classes: ClassProp<typeof styles>,
  { keyMetrics }: KeyMetrics,
  { setState, keyMetricView }: UseMerchantOverview,
  isExternalUser: boolean,
  locked = false,
  showDeltas: boolean,
) => ({
  label,
  key,
  hash,
  hidden = false,
  disabled = false,
  ...keyMetricStatProps
}: PageRoutes) => {
  const metric = (keyMetrics && keyMetrics[key]) || {};

  if (!metric || hidden) {
    return null;
  }

  const onClick = (hash: string) => () => {
    window.scrollTo({ top: isExternalUser ? 0 : 275, behavior: 'smooth' });
    setState({ keyMetricView: hash });
  };

  const KeyMetric =
    key === 'roas' ? (
      <ROASKeyMetrics
        label={label}
        keyMetric={metric}
        {...keyMetricStatProps}
        showDeltas={showDeltas}
      />
    ) : (
      <KeyMetricStat
        label={label}
        keyMetric={metric}
        {...keyMetricStatProps}
        showDeltas={showDeltas}
      />
    );

  const className = classnames(classes.tabItem, {
    locked,
    [classes.active]: hash === keyMetricView,
  });

  if (disabled || !metric.amount) {
    return (
      <Menu.Item key={key} className={className}>
        {KeyMetric}
      </Menu.Item>
    );
  }

  return (
    <Menu.Item
      onClick={onClick(hash)}
      className={className}
      to={`#${hash}`}
      as={NavLink}
      key={key}
    >
      {KeyMetric}
    </Menu.Item>
  );
};

const KeyMetrics: FunctionComponent<Props> = ({
  classes,
  keyMetrics,
  locked,
  showDeltas,
}) => {
  const merchantOverviewState = useMerchantOverview();
  const { isExternalUser, isDemoUser } = useUserMgmt();
  const { status: showAttribution } = useSplitIO('attribution_metric');

  return (
    <Menu className={classes.container}>
      {map(
        renderMetric(
          classes,
          keyMetrics || {},
          merchantOverviewState,
          isExternalUser,
          locked,
          showDeltas,
        ),
        getKeyMetrics(isExternalUser && !isDemoUser, showAttribution),
      )}
    </Menu>
  );
};

export default withStyles(styles)(KeyMetrics);
